/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Text, RenderCurrencyFormatTextField } from '@components/atoms'
import {
	FullModalLayout,
	SeasonProductToggleConfirmationModal,
} from '@components/molecules'

import {
	TextField,
	Switch,
	Button,
	Chip,
	InputAdornment,
	LinearProgress,
	MenuItem,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
} from '@mui/material'
import { mono, boShared } from '@components/systems/backoffice/backoffice.tokens'

import { useAppDispatch, useAppSelector, seasonActions, routeActions } from '@redux/index'
import {
	SeasonProductType,
	SeasonProductUpdateType,
	SeasonProductPhaseType,
	SeasonProductPhaseUpdateType,
} from '@schemas'

import { DatePicker } from '@mui/x-date-pickers/DatePicker'

import { useParams } from 'react-router-dom'
import dayjs from 'dayjs'

/**
 * 시즌 상품 수정 — 판매 조건 · 판매 단계 · 이행 로드맵.
 *
 * 내용이 많아 전체 화면(FullModalLayout)으로 띄운다. 라우트는
 * `MainLayout` children 이 아니라 형제로 둔다 — children 에 넣으면 레이아웃 안에 갇힌다.
 *
 * 프리오더를 켜야 아래 단계가 열린다. 프리오더 판매는 이렇게 흐른다 (CATALOG_PLAN §7.3):
 *   얼리버드 → 프리오더 → (종료) → 재시작 판매
 * "종료"는 저장하지 않는다. 프리오더가 끝났는데 재시작이 아직 열리지 않은 구간으로
 * 파생되며, 그동안은 무조건 품절이고 상점은 완판 화면으로 바뀐다.
 */

/** 판매 단계 — 기간과 값을 가진다 */
const SALE_PHASES = [
	{ key: 'earlybird', name: '얼리버드', optional: true, hasEnd: true },
	{ key: 'preorder', name: '프리오더', optional: false, hasEnd: true },
	{ key: 'restart', name: '재시작 판매', optional: true, hasEnd: false },
] as const

/** 이행 단계 — 시점만 있다. 로드맵으로 보여준다 */
const FULFILLMENT_PHASES = [
	{ key: 'part_order', name: '파트 주문', hint: '부품을 발주한 날' },
	{ key: 'production', name: '생산 시작', hint: '조립에 들어간 날' },
	{ key: 'delivery_start', name: '배송 시작', hint: '첫 출고일' },
	{ key: 'delivery_done', name: '배송 완료', hint: '마지막 배송이 끝난 날' },
] as const

/*
	값은 두 통화를 나란히 든다 (PRICING §7). 원화는 **부가세 포함가** — 결제가 10/110 으로
	역산한다. 재시작 판매는 값을 갖지 않고 시즌단가로 판다 — 서버가 저장 시 0 으로 강제하고
	조회가 0 을 시즌단가로 폴백한다(PRICING §5).
*/
type PhaseForm = {
	enabled: boolean
	startAt: string
	endAt: string
	usd: string
	krw: string
}

type BasicForm = {
	seasonUsd: string
	seasonKrw: string
	seasonDiscountUsd: string
	seasonQuantity: string
	seasonActiveYn: number
	preorderYn: number
	/** 재료원가(DISPATCH_PLAN §6.3). 빈 문자열 = 없음(→ 상품 관리 값) */
	materialCostUsd: string
	materialCostKrw: string
}

const emptyForm = (): PhaseForm => ({
	enabled: false,
	startAt: '',
	endAt: '',
	usd: '',
	krw: '',
})

const usd = (value: number): string =>
	value.toLocaleString('en-US', { style: 'currency', currency: 'USD' })

const krw = (value: number): string =>
	value.toLocaleString('ko-KR', { style: 'currency', currency: 'KRW' })

const toNumber = (value: string): number => (value === '' ? 0 : Number(value))

/** 폼은 'YYYY-MM-DD' 문자열로 들고 있고, DatePicker 만 Date 를 쓴다. */
const toDate = (value: string): Date | null =>
	value === '' ? null : dayjs(value).toDate()

const fromDate = (value: Date | null): string =>
	value === null || !dayjs(value).isValid() ? '' : dayjs(value).format('YYYY-MM-DD')

const toDateInput = (value: string): string =>
	value === '' || value === null ? '' : dayjs(value).format('YYYY-MM-DD')

/*
	`season` 은 redux-persist whitelist 에 있다(persistConfig.ts). sessionStorage 에 남아 있던
	**옛 슬라이스**가 복원되면 나중에 추가한 필드가 통째로 빠진 상태로 덮이므로,
	새 필드는 읽는 쪽에서 방어한다. `?? []` 를 selector 안에 두면 매번 새 배열이라
	리렌더가 돌기 때문에 참조가 고정된 상수를 쓴다.
*/
const EMPTY_PHASES: SeasonProductPhaseType[] = []
const EMPTY_PRODUCTS: SeasonProductType[] = []

const SeasonProductDetailPage = () => {
	// hook
	const { seasonId, seasonProductId } = useParams()

	// redux dispatch
	const dispatch = useAppDispatch()

	// redux state
	const storeSelected: SeasonProductType | null = useAppSelector(
		state => state.season.selectedSeasonProduct ?? null,
	)
	const seasonProducts: SeasonProductType[] = useAppSelector(
		state => state.season.seasonProducts ?? EMPTY_PRODUCTS,
	)
	const phases: SeasonProductPhaseType[] = useAppSelector(
		state => state.season.seasonProductPhases ?? EMPTY_PHASES,
	)
	const phaseLoading: boolean = useAppSelector(state => state.season.phaseLoading)

	/*
		목록(`seasonProducts`)이 정본이다. `selectedSeasonProduct` 는 목록에서 `자세히` 를 누른
		시점의 사본이라 저장 뒤에도 옛 값이고, sessionStorage 에 남아 새로고침 후 그대로 돌아온다 —
		원화를 저장했는데 새로고침하면 0 으로 보이던 원인. 목록을 먼저 보고 없을 때만 사본을 쓴다.
	*/
	const selected: SeasonProductType | null = React.useMemo(() => {
		const fromList = seasonProducts.find(aProduct => aProduct.id === seasonProductId)
		if (fromList !== undefined) return fromList
		if (storeSelected !== null && storeSelected.id === seasonProductId) {
			return storeSelected
		}
		return null
	}, [storeSelected, seasonProducts, seasonProductId])

	// state
	const [basicForm, setBasicForm] = React.useState<BasicForm>({
		seasonUsd: '',
		seasonKrw: '',
		seasonDiscountUsd: '',
		seasonQuantity: '',
		seasonActiveYn: 1,
		preorderYn: 0,
		materialCostUsd: '',
		materialCostKrw: '',
	})
	const [saleForm, setSaleForm] = React.useState<{ [key: string]: PhaseForm }>({
		earlybird: emptyForm(),
		preorder: emptyForm(),
		restart: emptyForm(),
	})
	const [fulfillForm, setFulfillForm] = React.useState<{ [key: string]: string }>({
		part_order: '',
		production: '',
		delivery_start: '',
		delivery_done: '',
	})
	/*
		스위치(프리오더 판매 · 얼리버드 · 재시작 판매)는 누르는 즉시 바꾸지 않고 한 번 묻는다.
		하나가 shop 의 가격 구조를 통째로 바꾼다.
	*/
	const [pendingToggle, setPendingToggle] = React.useState<{
		key: string
		label: string
		next: boolean
	} | null>(null)
	// 시즌에서 제외 — 목록 화면에 수정이 없어졌으므로 삭제도 여기서(2026-09-16)
	const [showRemove, setShowRemove] = React.useState<boolean>(false)

	React.useEffect(() => {
		if (seasonProductId !== undefined) {
			dispatch(seasonActions.FETCH_SEASON_PRODUCT_PHASE(seasonProductId))
		}
	}, [seasonProductId])

	// 들어올 때마다 목록을 서버에서 다시 받는다 — 복원된 옛 값으로 폼을 채우지 않기 위해
	React.useEffect(() => {
		if (seasonId !== undefined) {
			dispatch(seasonActions.FETCH_SEASON_PRODUCT(seasonId))
		}
	}, [seasonId])

	// 서버에서 읽은 시즌 상품을 폼으로 편다
	React.useEffect(() => {
		if (selected === null) return
		setBasicForm({
			seasonUsd: String(selected.seasonUsd ?? 0),
			seasonKrw: String(selected.seasonKrw ?? 0),
			seasonDiscountUsd: String(selected.seasonDiscountUsd ?? 0),
			seasonQuantity: String(selected.seasonQuantity ?? 0),
			seasonActiveYn: selected.seasonActiveYn ?? 1,
			preorderYn: selected.preorderYn ?? 0,
			materialCostUsd:
				selected.materialCostUsd === null || selected.materialCostUsd === undefined
					? ''
					: String(selected.materialCostUsd),
			materialCostKrw:
				selected.materialCostKrw === null || selected.materialCostKrw === undefined
					? ''
					: String(selected.materialCostKrw),
		})
	}, [selected])

	// 서버에서 읽은 단계를 폼으로 편다
	React.useEffect(() => {
		const nextSale: { [key: string]: PhaseForm } = {
			earlybird: emptyForm(),
			preorder: emptyForm(),
			restart: emptyForm(),
		}
		const nextFulfill: { [key: string]: string } = {
			part_order: '',
			production: '',
			delivery_start: '',
			delivery_done: '',
		}

		phases.forEach(aPhase => {
			if (aPhase.kind === 'sale' && nextSale[aPhase.phase] !== undefined) {
				nextSale[aPhase.phase] = {
					enabled: true,
					startAt: toDateInput(aPhase.startAt),
					endAt: toDateInput(aPhase.endAt),
					usd: String(aPhase.usd ?? 0),
					krw: String(aPhase.krw ?? 0),
				}
			}
			if (aPhase.kind === 'fulfillment' && nextFulfill[aPhase.phase] !== undefined) {
				nextFulfill[aPhase.phase] = toDateInput(aPhase.startAt)
			}
		})

		// 프리오더 구간은 늘 켜져 있는 것으로 둔다 — 프리오더를 켰다면 이 구간이 본체다
		nextSale.preorder.enabled = true

		setSaleForm(nextSale)
		setFulfillForm(nextFulfill)
	}, [phases])

	const onCloseClick = React.useCallback(() => {
		dispatch(routeActions.NAVIGATE_PATH('/main/season'))
	}, [])

	const editBasic = (field: keyof BasicForm, value: string | number) => {
		setBasicForm(prev => ({ ...prev, [field]: value }))
	}

	const editSale = (key: string, field: keyof PhaseForm, value: string | boolean) => {
		setSaleForm(prev => ({ ...prev, [key]: { ...prev[key], [field]: value } }))
	}

	const onRemoveConfirm = () => {
		if (selected === null) return
		dispatch(
			seasonActions.BATCH_UPDATE_SEASON_PRODUCT([{ iudFlag: 'D', id: selected.id }]),
		)
		setShowRemove(false)
		dispatch(routeActions.NAVIGATE_PATH('/main/season'))
	}

	const onToggleConfirm = () => {
		if (pendingToggle === null) return
		if (pendingToggle.key === 'preorder') {
			editBasic('preorderYn', pendingToggle.next ? 1 : 0)
		} else {
			editSale(pendingToggle.key, 'enabled', pendingToggle.next)
		}
		setPendingToggle(null)
	}

	const isPreorder: boolean = basicForm.preorderYn === 1
	const isAlwaysAvailable: boolean = (selected?.alwaysAvailableYn ?? 0) === 1

	/** 판매 방식 이름. 목록(`seasonProductList`)의 표와 같은 규칙이다. */
	const saleModeLabel: string = isAlwaysAvailable
		? isPreorder
			? '상시 프리오더'
			: '상시 판매'
		: isPreorder
		? '한정 프리오더'
		: '수량 한정'

	/** 지금이 어느 단계인가. 서버 판정과 같은 규칙으로 화면에서도 한 번 더 보여준다. */
	const currentPhaseKey: string = React.useMemo(() => {
		if (!isPreorder) return ''

		const today = dayjs().startOf('day')
		const inRange = (f: PhaseForm, hasEnd: boolean): boolean => {
			if (!f.enabled || f.startAt === '') return false
			if (today.isBefore(dayjs(f.startAt).startOf('day'))) return false
			if (hasEnd && f.endAt !== '' && today.isAfter(dayjs(f.endAt).endOf('day')))
				return false
			return true
		}
		if (inRange(saleForm.restart, false)) return 'restart'
		if (inRange(saleForm.preorder, true)) return 'preorder'
		if (inRange(saleForm.earlybird, true)) return 'earlybird'

		const po = saleForm.preorder
		if (po.enabled && po.endAt !== '' && today.isAfter(dayjs(po.endAt).endOf('day'))) {
			return 'closed'
		}
		return ''
	}, [saleForm, isPreorder])

	/*
		저장 전에 막을 것들. 2026-09-14 에 운영에서 **얼리버드 종료일이 시작일보다 하루 빠르고**
		재시작이 **시즌 종료 뒤**에 놓인 설정이 그대로 저장됐다. 둘 다 "영원히 오지 않는 구간"이라
		화면에서는 아무 일도 안 일어나는 것처럼 보인다 — 저장 시점에 잡아야 한다.
	*/
	const phaseErrors: string[] = React.useMemo(() => {
		if (!isPreorder) return []

		const errs: string[] = []
		const seasonStart = selected?.seasonStartDate ?? ''
		const seasonEnd = selected?.seasonEndDate ?? ''

		SALE_PHASES.forEach(aPhase => {
			const form = saleForm[aPhase.key]
			if (!form.enabled || form.startAt === '') return

			if (
				aPhase.hasEnd &&
				form.endAt !== '' &&
				dayjs(form.endAt).isBefore(form.startAt)
			) {
				errs.push(`${aPhase.name}: 종료일이 시작일보다 빠르다`)
			}
			if (seasonStart !== '' && dayjs(form.startAt).isBefore(dayjs(seasonStart), 'day')) {
				errs.push(
					`${aPhase.name}: 시작일이 시즌 시작(${toDateInput(seasonStart)}) 전이다`,
				)
			}
			if (seasonEnd !== '' && dayjs(form.startAt).isAfter(dayjs(seasonEnd), 'day')) {
				errs.push(
					`${aPhase.name}: 시작일이 시즌 종료(${toDateInput(
						seasonEnd,
					)}) 뒤라 열리지 않는다`,
				)
			}
			if (
				aPhase.hasEnd &&
				form.endAt !== '' &&
				seasonEnd !== '' &&
				dayjs(form.endAt).isAfter(dayjs(seasonEnd), 'day')
			) {
				errs.push(
					`${aPhase.name}: 종료일이 시즌 종료(${toDateInput(seasonEnd)})를 넘는다`,
				)
			}
		})

		// 얼리버드는 프리오더보다 먼저 끝나야 한다 — 겹치면 어느 값으로 팔리는지 흐려진다
		const eb = saleForm.earlybird
		const po = saleForm.preorder
		if (
			eb.enabled &&
			eb.endAt !== '' &&
			po.startAt !== '' &&
			!dayjs(eb.endAt).isBefore(po.startAt)
		) {
			errs.push('얼리버드 종료일이 프리오더 시작일보다 뒤다')
		}

		return errs
	}, [isPreorder, saleForm, selected])

	const onSaveClick = () => {
		if (seasonProductId === undefined || selected === null) return
		if (phaseErrors.length > 0) return

		/*
			두 벌을 따로 맡긴다 — 판매 조건은 SEASON_PRODUCT, 단계는 SEASON_PRODUCT_PHASE 다.
			날짜 4개(partOrderedAt 등)는 옛 컬럼이라 받은 값을 그대로 돌려보낸다. 빼면
			서버 updateList 가 null 로 덮는다.
		*/
		const basicPayload: SeasonProductUpdateType = {
			iudFlag: 'U',
			id: selected.id,
			seasonId: selected.seasonId,
			productId: selected.productId,
			usd: toNumber(basicForm.seasonUsd),
			krw: toNumber(basicForm.seasonKrw),
			quantity: toNumber(basicForm.seasonQuantity),
			discountUsd: toNumber(basicForm.seasonDiscountUsd),
			materialCostUsd:
				basicForm.materialCostUsd.trim() === ''
					? null
					: toNumber(basicForm.materialCostUsd),
			materialCostKrw:
				basicForm.materialCostKrw.trim() === ''
					? null
					: toNumber(basicForm.materialCostKrw),
			activeYn: basicForm.seasonActiveYn,
			preorderYn: basicForm.preorderYn,
			partOrderedAt: selected.partOrderedAt
				? dayjs(selected.partOrderedAt).format('YYYY-MM-DD')
				: '',
			productionStartAt: selected.productionStartAt
				? dayjs(selected.productionStartAt).format('YYYY-MM-DD')
				: '',
			deliveryStartAt: selected.deliveryStartAt
				? dayjs(selected.deliveryStartAt).format('YYYY-MM-DD')
				: '',
			deliveryCompletedAt: selected.deliveryCompletedAt
				? dayjs(selected.deliveryCompletedAt).format('YYYY-MM-DD')
				: '',
		}

		dispatch(seasonActions.BATCH_UPDATE_SEASON_PRODUCT([basicPayload]))

		const phaseList: SeasonProductPhaseUpdateType[] = []

		SALE_PHASES.forEach(aPhase => {
			const form = saleForm[aPhase.key]
			const exists = phases.some(p => p.phase === aPhase.key)

			// 프리오더를 껐으면 단계를 건드리지 않는다 — 행은 남기고 서버가 판정에서 뺀다.
			if (!isPreorder) return

			// 끈 단계는 지운다. 애초에 없던 것은 건드리지 않는다.
			if (!form.enabled || form.startAt === '') {
				if (exists) {
					phaseList.push({ iudFlag: 'D', phase: aPhase.key })
				}
				return
			}

			// 재시작은 값이 없다 — 시즌단가로 판다. 서버도 0 으로 강제한다.
			const isRestart: boolean = aPhase.key === 'restart'
			phaseList.push({
				iudFlag: exists ? 'U' : 'I',
				phase: aPhase.key,
				kind: 'sale',
				startAt: `${form.startAt} 00:00:00`,
				endAt: aPhase.hasEnd && form.endAt !== '' ? `${form.endAt} 23:59:59` : '',
				usd: isRestart ? 0 : toNumber(form.usd),
				krw: isRestart ? 0 : toNumber(form.krw),
			})
		})

		FULFILLMENT_PHASES.forEach(aPhase => {
			if (!isPreorder) return

			const value = fulfillForm[aPhase.key]
			const exists = phases.some(p => p.phase === aPhase.key)

			if (value === '') {
				if (exists) {
					phaseList.push({ iudFlag: 'D', phase: aPhase.key })
				}
				return
			}

			phaseList.push({
				iudFlag: exists ? 'U' : 'I',
				phase: aPhase.key,
				kind: 'fulfillment',
				startAt: `${value} 00:00:00`,
			})
		})

		if (phaseList.length > 0) {
			dispatch(
				seasonActions.BATCH_UPDATE_SEASON_PRODUCT_PHASE({
					seasonProductId: seasonProductId,
					phaseList: phaseList,
				}),
			)
		}
	}

	/** 로드맵 스텝. 위에서 아래로 진행한다 — 판매가 먼저, 이행이 뒤. */
	const roadmap = React.useMemo(() => {
		const today = dayjs().startOf('day')
		const saleSteps = SALE_PHASES.filter(p => saleForm[p.key].enabled).map(p => {
			const f = saleForm[p.key]
			return {
				key: p.key,
				name: p.name,
				at: f.startAt,
				until: p.hasEnd ? f.endAt : '',
				// 재시작은 시즌단가로 판다
				usd: p.key === 'restart' ? basicForm.seasonUsd : f.usd,
				kind: 'sale' as const,
				done: f.startAt !== '' && today.isAfter(dayjs(f.startAt)),
				active: currentPhaseKey === p.key,
			}
		})
		const fulfillSteps = FULFILLMENT_PHASES.map(p => ({
			key: p.key,
			name: p.name,
			at: fulfillForm[p.key],
			until: '',
			usd: '',
			kind: 'fulfillment' as const,
			done: fulfillForm[p.key] !== '' && today.isAfter(dayjs(fulfillForm[p.key])),
			active: false,
		}))
		return [...saleSteps, ...fulfillSteps]
	}, [saleForm, fulfillForm, currentPhaseKey, basicForm.seasonUsd])

	/*
		머리의 값 요약. "지금 적용되는 값" 하나만 보여주면 그게 얼리버드인지 시즌단가인지
		알 수 없다 — 단계 값을 전부 세우고 진행 중인 것에만 표시를 단다. 저장 전 폼 값이라
		고치는 대로 따라온다.
	*/
	const priceSummary = React.useMemo(() => {
		const seasonCell = {
			key: 'season',
			name: isPreorder ? '시즌단가 · 재시작' : '시즌단가',
			usd: toNumber(basicForm.seasonUsd),
			krw: toNumber(basicForm.seasonKrw),
			active: !isPreorder || currentPhaseKey === 'restart',
		}
		if (!isPreorder) return [seasonCell]

		const cells = SALE_PHASES.filter(
			p => p.key !== 'restart' && saleForm[p.key].enabled,
		).map(p => ({
			key: p.key,
			name: p.name,
			usd: toNumber(saleForm[p.key].usd),
			krw: toNumber(saleForm[p.key].krw),
			active: currentPhaseKey === p.key,
		}))
		return [...cells, seasonCell]
	}, [isPreorder, basicForm.seasonUsd, basicForm.seasonKrw, saleForm, currentPhaseKey])

	return (
		<FullModalLayout
			title="시즌 상품 수정"
			onCloseClick={onCloseClick}
			titleStyle={{ marginBottom: '1rem' }}
			/* 판매 단계 셋을 한 줄에 세워야 흐름이 보인다. 기본 폭에서는 두 칸으로 깨진다. */
			size="large"
		>
			<View css={style}>
				{phaseLoading && <LinearProgress className="loadingBar" />}

				{/* ── 어떤 상품인지 ───────────────────────────────────────── */}
				<View className="identityView">
					<View className="identityMain">
						<Text category="h4" className="identityName">
							{selected?.productName ?? '시즌 상품'}
						</Text>
						<Text category="p1" className="identityCode">
							{selected === null ? '' : selected.productCode}
						</Text>
					</View>
					<View className="identityChips">
						<Chip size="small" label={saleModeLabel} variant="outlined" />
						{currentPhaseKey !== '' && (
							<Chip
								size="small"
								label={
									currentPhaseKey === 'closed'
										? '프리오더 완판'
										: SALE_PHASES.find(p => p.key === currentPhaseKey)?.name ?? ''
								}
								variant={currentPhaseKey === 'closed' ? 'outlined' : 'filled'}
							/>
						)}
					</View>
					<View className="identitySpacer" />
					<View className="identityPrices">
						{priceSummary.map(aCell => (
							<View
								key={aCell.key}
								className={`identityPrice ${aCell.active ? 'active' : ''}`}
							>
								<View className="identityPriceHead">
									<Text category="p2" className="identityPriceLabel">
										{aCell.name}
									</Text>
									{aCell.active && (
										<Chip
											size="small"
											label={currentPhaseKey === 'closed' ? '완판 구간' : '지금'}
											variant={currentPhaseKey === 'closed' ? 'outlined' : 'filled'}
										/>
									)}
								</View>
								<Text className="identityPriceValue">{usd(aCell.usd)}</Text>
								<Text category="p2" className="identityPriceKrw">
									{krw(aCell.krw)}
								</Text>
							</View>
						))}
					</View>
				</View>

				{/* ── 판매 조건 ───────────────────────────────────────────── */}
				<View className="sectionView">
					<Text category="s1" className="sectionTitle">
						판매 조건
					</Text>

					<View className="cardView">
						<View className="fieldGrid">
							<TextField
								label="시즌단가 달러($)"
								value={basicForm.seasonUsd}
								onChange={e => editBasic('seasonUsd', e.target.value)}
								size="small"
								InputProps={{
									inputComponent: RenderCurrencyFormatTextField as any,
									startAdornment: <InputAdornment position="start">$</InputAdornment>,
								}}
								inputProps={{ decimalScale: 2, allowNegative: false }}
							/>
							{/*
								할인 전 가격. 실제 판매가는 시즌단가이고, 이 값은 shop 에서 줄을 긋는 데만 쓴다
								(PRICING §5). 결제·장바구니는 보지 않는다.
							*/}
							<TextField
								label="할인 전 가격 달러($)"
								value={basicForm.seasonDiscountUsd}
								onChange={e => editBasic('seasonDiscountUsd', e.target.value)}
								size="small"
								helperText="실제 판매가 = 시즌단가. 이 값이 더 높으면 shop 에서 할인 형식으로 표시"
								InputProps={{
									inputComponent: RenderCurrencyFormatTextField as any,
									startAdornment: <InputAdornment position="start">$</InputAdornment>,
								}}
								inputProps={{ decimalScale: 2, allowNegative: false }}
							/>
							<TextField
								type="number"
								label="수량"
								value={basicForm.seasonQuantity}
								onChange={e => editBasic('seasonQuantity', e.target.value)}
								size="small"
								disabled={isAlwaysAvailable}
								helperText={isAlwaysAvailable ? '상시 판매는 수량 제한이 없다' : ''}
								inputProps={{ min: 0, step: 1 }}
							/>
							{/* 다른 필드와 같은 모양으로 — 혼자 다르면 눈이 걸린다 */}
							{/* 품절처리 = ACTIVE_YN 의 반대. Yes 면 shop 에 Sold out 으로 나간다 */}
							<TextField
								select
								label="품절처리"
								value={basicForm.seasonActiveYn === 0 ? 1 : 0}
								onChange={e =>
									editBasic('seasonActiveYn', Number(e.target.value) === 1 ? 0 : 1)
								}
								size="small"
							>
								<MenuItem value={1}>Yes</MenuItem>
								<MenuItem value={0}>No</MenuItem>
							</TextField>
						</View>

						{/* 원화는 달러와 줄을 나눈다 */}
						<View className="fieldGrid krwRow">
							<TextField
								label="시즌단가 원화(₩)"
								value={basicForm.seasonKrw}
								onChange={e => editBasic('seasonKrw', e.target.value)}
								size="small"
								InputProps={{
									inputComponent: RenderCurrencyFormatTextField as any,
									startAdornment: <InputAdornment position="start">₩</InputAdornment>,
								}}
								inputProps={{ decimalScale: 0, allowNegative: false }}
							/>
						</View>

						{/*
							재료원가 — 정산(부가세·후원금 적립)이 판매일의 시즌에서 먼저 읽는 원가(DISPATCH_PLAN §6.3).
							비우면 상품 관리의 재료원가를 쓴다. 판매가와 무관.
						*/}
						<View className="fieldGrid krwRow">
							<TextField
								label="재료원가 달러($)"
								value={basicForm.materialCostUsd}
								onChange={e => editBasic('materialCostUsd', e.target.value)}
								size="small"
								helperText="비우면 상품 관리의 재료원가"
								InputProps={{
									inputComponent: RenderCurrencyFormatTextField as any,
									startAdornment: <InputAdornment position="start">$</InputAdornment>,
								}}
								inputProps={{ decimalScale: 2, allowNegative: false }}
							/>
							<TextField
								label="재료원가 원화(₩)"
								value={basicForm.materialCostKrw}
								onChange={e => editBasic('materialCostKrw', e.target.value)}
								size="small"
								helperText="정산 원가의 첫 출처"
								InputProps={{
									inputComponent: RenderCurrencyFormatTextField as any,
									startAdornment: <InputAdornment position="start">₩</InputAdornment>,
								}}
								inputProps={{ decimalScale: 0, allowNegative: false }}
							/>
						</View>

						<View className="toggleRow">
							<Text category="s2" className="toggleLabel">
								프리오더 판매
							</Text>
							<Switch
								checked={isPreorder}
								onChange={e =>
									setPendingToggle({
										key: 'preorder',
										label: '프리오더 판매',
										next: e.target.checked,
									})
								}
							/>
						</View>
					</View>
				</View>

				{isPreorder && (
					<>
						{/* ── 판매 단계 ───────────────────────────────────── */}
						<View className="sectionView">
							<View className="sectionHeadRow">
								<Text category="s1" className="sectionTitle">
									판매 단계
								</Text>
								<Text category="p2" className="sectionHint">
									얼리버드 → 프리오더 → 종료 → 재시작
								</Text>
							</View>

							<View className="phaseGrid">
								{SALE_PHASES.map(aPhase => {
									const form = saleForm[aPhase.key]
									const isActive = currentPhaseKey === aPhase.key
									return (
										<View
											key={aPhase.key}
											className={`phaseCard ${isActive ? 'active' : ''} ${
												form.enabled ? '' : 'off'
											}`}
										>
											<View className="phaseCardHead">
												<View className="phaseCardTitle">
													<Text category="s1" className="phaseName">
														{aPhase.name}
													</Text>
													{isActive && <Chip size="small" label="진행 중" />}
												</View>
												{aPhase.optional && (
													<Switch
														size="small"
														checked={form.enabled}
														onChange={e =>
															setPendingToggle({
																key: aPhase.key,
																label: aPhase.name,
																next: e.target.checked,
															})
														}
													/>
												)}
											</View>

											<View className="phaseFields">
												<DatePicker
													label="시작일"
													value={toDate(form.startAt)}
													onChange={v => editSale(aPhase.key, 'startAt', fromDate(v))}
													disabled={!form.enabled}
													format="yyyy-MM-dd"
													slotProps={{ textField: { size: 'small' } }}
												/>
												{aPhase.hasEnd && (
													<DatePicker
														label="종료일"
														value={toDate(form.endAt)}
														onChange={v => editSale(aPhase.key, 'endAt', fromDate(v))}
														disabled={!form.enabled}
														format="yyyy-MM-dd"
														slotProps={{ textField: { size: 'small' } }}
													/>
												)}
												{aPhase.key === 'restart' ? (
													/* 재시작은 시즌단가로 판다. 여기서는 읽기만 */
													<>
														<TextField
															label="시즌단가 달러($)"
															value={basicForm.seasonUsd}
															disabled
															size="small"
															InputProps={{
																inputComponent: RenderCurrencyFormatTextField as any,
																readOnly: true,
																startAdornment: (
																	<InputAdornment position="start">$</InputAdornment>
																),
															}}
															inputProps={{ decimalScale: 2 }}
														/>
														<TextField
															label="시즌단가 원화(₩)"
															value={basicForm.seasonKrw}
															disabled
															size="small"
															InputProps={{
																inputComponent: RenderCurrencyFormatTextField as any,
																readOnly: true,
																startAdornment: (
																	<InputAdornment position="start">₩</InputAdornment>
																),
															}}
															inputProps={{ decimalScale: 0 }}
														/>
													</>
												) : (
													<>
														<TextField
															label="판매가 달러($)"
															value={form.usd}
															onChange={e => editSale(aPhase.key, 'usd', e.target.value)}
															disabled={!form.enabled}
															size="small"
															InputProps={{
																inputComponent: RenderCurrencyFormatTextField as any,
																startAdornment: (
																	<InputAdornment position="start">$</InputAdornment>
																),
															}}
															inputProps={{ decimalScale: 2, allowNegative: false }}
														/>
														<TextField
															label="판매가 원화(₩)"
															value={form.krw}
															onChange={e => editSale(aPhase.key, 'krw', e.target.value)}
															disabled={!form.enabled}
															size="small"
															InputProps={{
																inputComponent: RenderCurrencyFormatTextField as any,
																startAdornment: (
																	<InputAdornment position="start">₩</InputAdornment>
																),
															}}
															inputProps={{ decimalScale: 0, allowNegative: false }}
														/>
													</>
												)}
											</View>
										</View>
									)
								})}
							</View>

							<View className="noticeView">
								<Text category="p2">
									프리오더 종료일부터 재시작 시작일까지는 품절로 잠긴다.
								</Text>
							</View>
						</View>

						{/* ── 이행 로드맵 ─────────────────────────────────── */}
						<View className="sectionView">
							<View className="sectionHeadRow">
								<Text category="s1" className="sectionTitle">
									이행 로드맵
								</Text>
							</View>

							<View className="roadmapView">
								{roadmap.map((aStep, idx) => (
									<View
										key={aStep.key}
										className={`roadmapStep ${aStep.done ? 'done' : ''} ${
											aStep.active ? 'active' : ''
										}`}
									>
										<View className="roadmapRail">
											<View className="roadmapDot" />
											{idx < roadmap.length - 1 && <View className="roadmapLine" />}
										</View>
										<View className="roadmapBody">
											<View className="roadmapTitleRow">
												<Text category="s2" className="roadmapName">
													{aStep.name}
												</Text>
												{aStep.active && <Chip size="small" label="진행 중" />}
											</View>
											{aStep.kind === 'sale' ? (
												<Text category="p2" className="roadmapMeta">
													{`${
														aStep.at === ''
															? '날짜 미정'
															: aStep.until !== ''
															? `${aStep.at} ~ ${aStep.until}`
															: `${aStep.at} ~`
													}${
														aStep.usd !== '' && aStep.usd !== '0'
															? `   ·   $${aStep.usd}`
															: ''
													}`}
												</Text>
											) : (
												/* 이행 일정은 로드맵 안에서 바로 고친다 — 같은 것을 두 번 적지 않는다 */
												<DatePicker
													value={toDate(fulfillForm[aStep.key])}
													onChange={v =>
														setFulfillForm(prev => ({
															...prev,
															[aStep.key]: fromDate(v),
														}))
													}
													format="yyyy-MM-dd"
													slotProps={{
														textField: { size: 'small', className: 'roadmapDateField' },
													}}
												/>
											)}
										</View>
									</View>
								))}
							</View>
						</View>
					</>
				)}

				<SeasonProductToggleConfirmationModal
					open={pendingToggle !== null}
					onClose={() => setPendingToggle(null)}
					onConfirm={onToggleConfirm}
					label={pendingToggle?.label ?? ''}
					next={pendingToggle?.next ?? false}
				/>

				<Dialog open={showRemove} onClose={() => setShowRemove(false)}>
					<DialogTitle>이 시즌에서 제외</DialogTitle>
					<DialogContent>
						<DialogContentText>
							<strong>{selected?.productName ?? ''}</strong> 을(를) 이 시즌에서 뺍니다.
							판매 단계와 값도 같이 지워지고, shop 목록에서 바로 사라집니다.
						</DialogContentText>
					</DialogContent>
					<DialogActions>
						<Button color="inherit" onClick={() => setShowRemove(false)}>
							취소
						</Button>
						<Button variant="outlined" onClick={onRemoveConfirm}>
							제외
						</Button>
					</DialogActions>
				</Dialog>

				<View className="footerView">
					<Button
						variant="text"
						onClick={() => setShowRemove(true)}
						disabled={selected === null}
					>
						이 시즌에서 제외
					</Button>
					{phaseErrors.length > 0 && (
						<View className="errorView">
							{phaseErrors.map(anError => (
								<Text key={anError} category="p2" className="errorText">
									{anError}
								</Text>
							))}
						</View>
					)}
					<View className="footerSpacer" />
					<Button
						variant="contained"
						onClick={onSaveClick}
						disabled={phaseLoading || selected === null || phaseErrors.length > 0}
					>
						저장
					</Button>
				</View>
			</View>
		</FullModalLayout>
	)
}

export default SeasonProductDetailPage

const style = css({
	...boShared,
	display: 'flex',
	flexDirection: 'column',
	gap: 0,
	paddingBottom: 64,

	/*
		Text 컴포넌트는 `div > p` 구조이고 마진이 안쪽 p 에, 정렬은 가운데가 기본이다.
		그대로 두면 설명이 화면 한복판에 떠서 어느 필드에 딸린 말인지 알 수 없다.
		이 화면의 글은 전부 왼쪽에서 시작한다.
	*/
	p: {
		margin: 0,
		textAlign: 'left',
		fontFamily: 'inherit',
	},

	'.loadingBar': {
		width: '100%',
	},

	// 어떤 상품인지 — 화면에 들어오면 이것부터 읽힌다. 헤어라인 밴드
	'.identityView': {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		flexWrap: 'wrap',
		gap: 16,
		padding: '0 0 24px',
		borderRadius: 0,
		backgroundColor: 'transparent',
		borderBottom: `1px solid ${mono.line}`,
	},
	'.identityMain': {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'baseline',
		gap: 10,

		'.identityName': {
			fontSize: 22,
			fontWeight: 300,
			color: mono.ink,
			width: 'auto',
		},
		'.identityCode': {
			fontSize: 11,
			fontWeight: 500,
			color: mono.faint,
			textTransform: 'uppercase',
			letterSpacing: '.14em',
			width: 'auto',
		},
	},
	'.identityChips': {
		display: 'flex',
		flexDirection: 'row',
		gap: 6,
	},
	'.identitySpacer': {
		flex: 1,
	},
	// 값 요약 — 단계 값을 전부 세우고 진행 중인 칸만 외곽선을 ink 로 올린다
	'.identityPrices': {
		display: 'flex',
		flexDirection: 'row',
		flexWrap: 'wrap',
		gap: 8,
	},
	'.identityPrice': {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-end',
		gap: 4,
		minWidth: '9rem',
		padding: '10px 14px',
		borderRadius: 0,
		border: `1px solid ${mono.line}`,
		backgroundColor: mono.surface,

		'&.active': {
			borderColor: mono.ink,
			boxShadow: 'none',
		},

		'.identityPriceHead': {
			display: 'flex',
			flexDirection: 'row',
			alignItems: 'center',
			gap: 6,
		},
		'.identityPriceLabel': {
			fontSize: 11,
			fontWeight: 500,
			letterSpacing: '.14em',
			textTransform: 'uppercase',
			color: mono.faint,
		},
		'.identityPriceValue': {
			fontSize: 24,
			lineHeight: 1,
			fontWeight: 300,
			color: mono.ink,
			fontVariantNumeric: 'tabular-nums',
		},
		'.identityPriceKrw': {
			fontSize: 12,
			fontWeight: 300,
			color: mono.dim,
			fontVariantNumeric: 'tabular-nums',
		},
	},

	'.sectionView': {
		display: 'flex',
		flexDirection: 'column',
		gap: 14,
		padding: '24px 0',
		borderBottom: `1px solid ${mono.line}`,
	},
	'.sectionHeadRow': {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'baseline',
		gap: 12,
	},
	'.sectionTitle': {
		fontSize: 11,
		fontWeight: 500,
		letterSpacing: '.24em',
		textTransform: 'uppercase',
		color: mono.faint,
		width: 'auto',
	},
	'.sectionHint': {
		fontSize: 12.5,
		fontWeight: 300,
		color: mono.dim,
	},

	'.cardView': {
		display: 'flex',
		flexDirection: 'column',
		gap: 16,
		padding: 0,
		borderRadius: 0,
		border: 0,
		backgroundColor: 'transparent',
	},

	// 필드는 같은 폭으로 줄을 맞춘다. 좁아지면 칸 수가 준다.
	'.fieldGrid': {
		display: 'grid',
		gridTemplateColumns: 'repeat(auto-fit, minmax(11rem, 1fr))',
		gap: 16,

		// 원화 줄은 칸 수를 윗줄과 같게 유지한다(auto-fill) — 입력 폭이 달러 칸과 같아진다
		'&.krwRow': {
			gridTemplateColumns: 'repeat(auto-fill, minmax(11rem, 1fr))',
			paddingTop: 16,
			borderTop: `1px solid ${mono.line}`,
		},
	},

	// 라벨은 왼쪽, 스위치는 오른쪽 끝
	'.toggleRow': {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		paddingTop: 12,
		borderTop: `1px solid ${mono.line}`,

		'.toggleLabel': {
			fontSize: 13,
			fontWeight: 500,
			color: mono.ink,
		},
	},

	// 판매 단계 — 세 구간을 나란히 놓아야 흐름이 보인다
	'.phaseGrid': {
		display: 'grid',
		gridTemplateColumns: 'repeat(auto-fit, minmax(15rem, 1fr))',
		gap: 12,
		alignItems: 'stretch',
	},
	'.phaseCard': {
		display: 'flex',
		flexDirection: 'column',
		gap: 6,
		padding: 16,
		border: `1px solid ${mono.line}`,
		borderRadius: 0,
		backgroundColor: mono.surface,
		transition: 'border-color 200ms ease-out, opacity 200ms ease-out',

		'&.off': {
			backgroundColor: 'transparent',
			opacity: 0.6,
		},
		'&.active': {
			borderColor: mono.ink,
			boxShadow: 'none',
		},

		'.phaseCardHead': {
			display: 'flex',
			flexDirection: 'row',
			alignItems: 'center',
			justifyContent: 'space-between',
			gap: 8,
			minHeight: 32,
		},
		'.phaseCardTitle': {
			display: 'flex',
			flexDirection: 'row',
			alignItems: 'center',
			gap: 8,
		},
		'.phaseName': {
			fontSize: 14,
			fontWeight: 500,
			color: mono.ink,
			width: 'auto',
		},
		'.phaseFields': {
			display: 'flex',
			flexDirection: 'column',
			gap: 10,
			paddingTop: 4,
		},
	},

	'.noticeView': {
		padding: '12px 0 0',
		borderRadius: 0,
		backgroundColor: 'transparent',
		color: mono.dim,
		fontSize: 12.5,
		fontWeight: 300,
	},

	// 이행 로드맵 — 세로로 내려가고, 날짜는 여기서 바로 고친다
	'.roadmapView': {
		display: 'flex',
		flexDirection: 'column',
		width: '100%',
		padding: '8px 0 0',
		borderRadius: 0,
		border: 0,
		backgroundColor: 'transparent',
	},
	'.roadmapStep': {
		display: 'flex',
		flexDirection: 'row',
		gap: 16,
		// 행마다 높이가 다르면 연결선이 끊겨 보인다. 날짜 입력이 없는 행도 같은 높이로.
		minHeight: 52,

		'.roadmapRail': {
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			width: 16,
			flexShrink: 0,
		},
		'.roadmapDot': {
			width: 8,
			height: 8,
			marginTop: 16,
			borderRadius: '50%',
			border: `1px solid ${mono.lineInput}`,
			backgroundColor: mono.surface,
			flexShrink: 0,
		},
		'.roadmapLine': {
			flex: 1,
			width: 1,
			marginTop: 4,
			backgroundColor: mono.line,
		},
		/*
			이름과 값을 가로로 붙인다. 세로로 쌓으면 오른쪽이 통째로 비고 목록이 두 배로
			길어진다 — 로드맵은 한눈에 훑는 것이라 짧을수록 좋다.
		*/
		'.roadmapBody': {
			display: 'flex',
			flexDirection: 'row',
			alignItems: 'center',
			gap: 16,
			paddingBottom: 12,
			minWidth: 0,
			flex: 1,
		},
		'.roadmapTitleRow': {
			display: 'flex',
			flexDirection: 'row',
			alignItems: 'center',
			gap: 8,
			width: '9rem',
			flexShrink: 0,
		},
		'.roadmapName': {
			fontSize: 13,
			fontWeight: 400,
			color: mono.faint,
			width: 'auto',
		},
		'.roadmapMeta': {
			fontSize: 12.5,
			fontWeight: 300,
			color: mono.faint,
			fontVariantNumeric: 'tabular-nums',
		},
		'.roadmapDateField': {
			width: '11rem',
		},

		// 지난 단계 = 채운 점, 진행 중 = ink 외곽 점. 색이 아니라 채움으로 가른다
		'&.done': {
			'.roadmapDot': {
				borderColor: mono.ink,
				backgroundColor: mono.ink,
			},
			'.roadmapLine': {
				backgroundColor: mono.ink,
			},
			'.roadmapName': {
				color: mono.ink,
			},
			'.roadmapMeta': {
				color: mono.dim,
			},
		},
		'&.active': {
			'.roadmapDot': {
				borderColor: mono.ink,
				backgroundColor: mono.surface,
				boxShadow: `inset 0 0 0 1px ${mono.surface}, inset 0 0 0 3px ${mono.ink}`,
			},
			'.roadmapName': {
				color: mono.ink,
				fontWeight: 500,
			},
		},
	},

	'.footerView': {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		gap: 16,
		paddingTop: 20,
	},
	'.footerSpacer': {
		flex: 1,
	},
	'.errorView': {
		display: 'flex',
		flexDirection: 'column',
		gap: 2,
		padding: 0,
		borderRadius: 0,
		backgroundColor: 'transparent',
		border: 0,
	},
	'.errorText': {
		fontSize: 12,
		color: mono.error,
	},
})
